import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faArrowDown,
  faGamepad,
  faCode,
  faGlobe,
  faRobot,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import './FreeTrialLanding.css';

const FALLBACK_STATS = { students: 300, classes: 7900 };

// Always rounded DOWN so the claim stays true (315 -> 300+)
const roundDown = (value, step) => Math.floor(Number(value) / step) * step;
const formatPlus = (n) => `${n.toLocaleString('en-US')}+`;

const TOPICS = [
  { icon: faGamepad, text: 'Games & apps' },
  { icon: faCode, text: 'Python' },
  { icon: faGlobe, text: 'Web development' },
  { icon: faRobot, text: 'AI & robotics' },
];

const FORM_ID = 'free-trial-form'; // rendered by <FreeTrial />

const scrollToForm = () => {
  const form = document.getElementById(FORM_ID);
  if (!form) return;
  const offset = window.innerWidth <= 768 ? 70 : 100;
  const top = form.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: 'smooth' });
};

const FreeTrialLanding = () => {
  const [stats, setStats] = useState(FALLBACK_STATS);
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Live numbers, same as the homepage hero
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/class-stats`);
        if (!res.ok) return;
        const data = await res.json();
        const students = roundDown(data.studentsEnrolled, 50);
        const classes = roundDown(data.classesCompleted, 100);
        if (!cancelled && students > 0 && classes > 0) setStats({ students, classes });
      } catch {
        // keep fallback numbers
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mobile sticky button: only while the booking form is off screen
  useEffect(() => {
    const form = document.getElementById(FORM_ID);
    if (!form || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setShowBar(!entry.isIntersecting && window.scrollY > 200),
      { threshold: 0.05 }
    );
    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section className="rmftl" aria-labelledby="rmftl-title">
        <div className="rmftl-inner">
          <span className="rmftl-badge">
            <span className="rmftl-dot" aria-hidden="true" />
            Free trial class
          </span>

          <h1 id="rmftl-title" className="rmftl-title">
            Book a <span>free 1-to-1 class</span> for your child
          </h1>

          <p className="rmftl-sub">
            Live online with a real teacher. Ages 6–17, no experience needed.
          </p>

          <ul className="rmftl-topics">
            {TOPICS.map((t) => (
              <li key={t.text}>
                <FontAwesomeIcon icon={t.icon} />
                {t.text}
              </li>
            ))}
          </ul>

          <div className="rmftl-proof">
            <span>
              <FontAwesomeIcon icon={faClock} /> 50 minutes
            </span>
            <span>
              <FontAwesomeIcon icon={faCheck} /> Free, no commitment
            </span>
            <span>
              <strong>{formatPlus(stats.students)}</strong> students
            </span>
            <span>
              <strong>{formatPlus(stats.classes)}</strong> classes taught
            </span>
          </div>
        </div>
      </section>

      <div className={`rmftl-bar ${showBar ? 'is-visible' : ''}`} aria-hidden={!showBar}>
        <button type="button" className="rmftl-bar-btn" onClick={scrollToForm} tabIndex={showBar ? 0 : -1}>
          Book the free class
          <FontAwesomeIcon icon={faArrowDown} />
        </button>
      </div>
    </>
  );
};

export default FreeTrialLanding;